import type { ProviderQuote } from '../../../features/catalog/model';
import { fmtReq, pct } from '../../../lib/format';
import { servedCount } from '../ProviderRow';
import { m } from '../../../paraglide/messages';

/** Where a row stands in the demonstration call. `rest` is the static look. */
export type RowTone = 'rest' | 'calling' | 'failed' | 'answered';

const COLOR = 'transition-[color,background-color,border-color,box-shadow] duration-300';

export const Marker = ({ position, tone }: { position: number | null; tone: RowTone }) => {
  if (position === null) {
    return <span className="relative z-10 size-7 rounded-full border border-dashed border-rule bg-surface" />;
  }
  const look =
    tone === 'failed'
      ? 'border border-neg bg-surface text-neg'
      : tone === 'calling'
        ? 'bg-accent text-accent-ink ring-4 ring-accent/20'
        : position === 0 || tone === 'answered'
          ? 'bg-accent text-accent-ink'
          : 'border border-rule bg-surface text-text-3';
  return (
    <span
      className={`mono relative z-10 grid size-7 place-items-center rounded-full text-12 font-medium ${COLOR} ${look}`}
    >
      {position + 1}
    </span>
  );
};

const CHIP = 'mono w-fit whitespace-nowrap rounded-md border px-1.5 py-px text-10';

export const Role = ({ position, tone }: { position: number | null; tone: RowTone }) => {
  if (position === null) {
    return <span className={`${CHIP} border-rule bg-surface-3 text-text-3`}>{m.endpoint_provider_not_serving()}</span>;
  }
  if (tone === 'failed') return <span className={`${CHIP} border-neg text-neg`}>{m.endpoint_role_failed()}</span>;
  if (tone === 'answered') {
    return <span className={`${CHIP} border-accent-line bg-accent-soft text-accent`}>{m.endpoint_role_answered()}</span>;
  }
  if (position === 0) {
    return <span className={`${CHIP} border-accent-line bg-accent-soft text-accent`}>{m.endpoint_role_first()}</span>;
  }
  // The transparent border keeps the line as tall as a chip, so a row turning
  // "Answered" does not grow.
  return (
    <span className="mono w-fit whitespace-nowrap border border-transparent py-px text-10 text-text-3">
      {m.endpoint_role_backup({ position })}
    </span>
  );
};

export const Served = ({ provider, share, first }: { provider: ProviderQuote; share: number; first: boolean }) => (
  <span className="flex min-w-0 flex-col gap-1">
    <span className="flex items-center gap-2.5">
      <span className="h-1.5 flex-1 overflow-hidden rounded-2 bg-surface-3">
        {share > 0 ? (
          <span
            className={`block h-full rounded-2 ${first ? 'bg-accent' : 'bg-text-3'}`}
            style={{ width: `${Math.max(share * 100, 2)}%` }}
          />
        ) : null}
      </span>
      <span className={`mono w-13 shrink-0 text-right text-11 tabular-nums ${first ? 'text-text' : 'text-text-3'}`}>
        {share > 0 ? pct(share * 100) : '-'}
      </span>
    </span>
    {servedCount(provider) > 0 ? (
      <span className="mono text-10 text-text-3">{m.endpoint_role_calls({ count: fmtReq(servedCount(provider)) })}</span>
    ) : null}
  </span>
);
